import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import CurrencyInput from '../commons/CurrencyInput';
import { formatValue } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errors';
import {
  getAnnualBudget,
  setBudgetCell,
  createRecurringExpense,
  deleteRecurringExpense,
} from '../../api/wallet';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const SUMMARY_WIDTH = 116;

const stickyCol = {
  position: 'sticky',
  left: 0,
  bgcolor: 'background.paper',
  zIndex: 2,
  minWidth: 180,
};

const stickyRightBase = {
  position: 'sticky',
  bgcolor: 'background.paper',
  zIndex: 2,
  minWidth: SUMMARY_WIDTH,
  whiteSpace: 'nowrap',
};

const stickyTotal = {
  ...stickyRightBase,
  right: SUMMARY_WIDTH,
  borderLeft: '1px solid',
  borderColor: 'divider',
};
const stickyMedia = { ...stickyRightBase, right: 0 };

const AnnualBudget = ({ initialYear }) => {
  const [year, setYear] = useState(initialYear || new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { itemId, month }
  const [editValue, setEditValue] = useState(0);
  const [newName, setNewName] = useState('');
  const [newCurrency, setNewCurrency] = useState('BRL');
  const [error, setError] = useState('');

  const showError = (err, fallback) => setError(getErrorMessage(err, fallback));

  const fetchBudget = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAnnualBudget(year);
      setRows(response.data?.Rows || []);
    } catch (err) {
      showError(err, 'Não foi possível carregar o planejamento.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchBudget();
  }, [fetchBudget]);

  const openEditor = (item, cell) => {
    if (cell.IsRealized) return; // realized amounts are not editable here
    // Start fresh at zero: CurrencyInput accumulates cents per keystroke, so
    // the user types the whole amount (same UX as the fast-entry row).
    setEditValue(0);
    setEditing({ itemId: item.ID, month: cell.Month });
  };

  const applyCellLocally = (itemId, month, value) =>
    setRows((prev) =>
      prev.map((row) => {
        if (row.Item.ID !== itemId) return row;
        const cells = row.Cells.map((c) =>
          c.Month === month ? { ...c, Value: value, IsProjected: true, IsRealized: false } : c
        );
        const total = cells.reduce((sum, c) => sum + (Number(c.Value) || 0), 0);
        return { ...row, Cells: cells, Total: total, Media: total / 12 };
      })
    );

  const saveCell = async (item, month) => {
    if (!isEditing(item.ID, month)) return; // guard against double save (Enter + blur)
    const value = editValue;
    setEditing(null);
    applyCellLocally(item.ID, month, value);
    try {
      await setBudgetCell({
        RecurringExpenseId: item.ID,
        Year: year,
        Month: month,
        PlannedValue: String(value),
        Currency: item.Currency,
      });
    } catch (err) {
      showError(err, 'Falha ao salvar a projeção.');
    }
  };

  const handleAddItem = async () => {
    if (!newName.trim()) {
      setError('Informe o nome do item.');
      return;
    }
    try {
      await createRecurringExpense({
        Name: newName.trim(),
        Currency: newCurrency,
        IsMandatory: false,
        Order: rows.length,
      });
      setNewName('');
      await fetchBudget();
    } catch (err) {
      showError(err, 'Falha ao adicionar o item.');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await deleteRecurringExpense(id);
      await fetchBudget();
    } catch (err) {
      showError(err, 'Falha ao remover o item.');
    }
  };

  const isEditing = (itemId, month) => editing?.itemId === itemId && editing?.month === month;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Planejamento Anual</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" aria-label="ano anterior" onClick={() => setYear((y) => y - 1)}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 64, textAlign: 'center' }}>
            {year}
          </Typography>
          <IconButton size="small" aria-label="próximo ano" onClick={() => setYear((y) => y + 1)}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Nome do item"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          size="small"
          sx={{ width: 220 }}
        />
        <TextField
          select
          value={newCurrency}
          onChange={(e) => setNewCurrency(e.target.value)}
          size="small"
          sx={{ width: 100 }}
        >
          <MenuItem value="BRL">BRL</MenuItem>
          <MenuItem value="USD">USD</MenuItem>
        </TextField>
        <Button variant="contained" size="small" onClick={handleAddItem}>
          Adicionar item
        </Button>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 6 }}>
          Nenhum item recorrente. Adicione o primeiro acima.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...stickyCol, fontWeight: 600 }}>Item</TableCell>
                {MONTHS.map((m) => (
                  <TableCell key={m} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    {m}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ ...stickyTotal, fontWeight: 600 }}>Total</TableCell>
                <TableCell align="right" sx={{ ...stickyMedia, fontWeight: 600 }}>Média</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.Item.ID} hover>
                  <TableCell sx={{ ...stickyCol }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{row.Item.Name}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.Item.Currency}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`remover-${row.Item.ID}`}
                        onClick={() => handleDeleteItem(row.Item.ID)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  {row.Cells.map((cell) => (
                    <TableCell
                      key={cell.Month}
                      align="right"
                      aria-label={`cell-${row.Item.ID}-${cell.Month}`}
                      data-realized={String(!!cell.IsRealized)}
                      onClick={() => openEditor(row.Item, cell)}
                      sx={{
                        cursor: cell.IsRealized ? 'default' : 'pointer',
                        color: cell.IsRealized ? 'text.primary' : 'warning.main',
                        fontStyle: cell.IsRealized ? 'normal' : 'italic',
                        whiteSpace: 'nowrap',
                        '&:hover': cell.IsRealized ? {} : { bgcolor: 'action.hover' },
                      }}
                    >
                      {isEditing(row.Item.ID, cell.Month) ? (
                        <Box
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveCell(row.Item, cell.Month);
                            if (e.key === 'Escape') setEditing(null);
                          }}
                        >
                          <CurrencyInput
                            value={editValue}
                            onChange={setEditValue}
                            currency={row.Item.Currency}
                            label=""
                            size="small"
                            variant="standard"
                            autoFocus
                            onBlur={() => saveCell(row.Item, cell.Month)}
                            inputProps={{ 'aria-label': `edit-${row.Item.ID}-${cell.Month}` }}
                          />
                        </Box>
                      ) : (
                        formatValue(Number(cell.Value) || 0, row.Item.Currency)
                      )}
                    </TableCell>
                  ))}

                  <TableCell align="right" aria-label={`total-${row.Item.ID}`} sx={{ ...stickyTotal, fontWeight: 600 }}>
                    {formatValue(Number(row.Total) || 0, row.Item.Currency)}
                  </TableCell>
                  <TableCell align="right" aria-label={`media-${row.Item.ID}`} sx={{ ...stickyMedia, color: 'text.secondary' }}>
                    {formatValue(Number(row.Media) || 0, row.Item.Currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AnnualBudget;
